import { Check, GripVertical, Trash2, Clock, Pencil, Star, Play, Pause } from "lucide-react";
import { useDraggable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { toggleTaskDone, deleteTask, findCategory, useStore, startTimer, stopTimer, toggleTaskPriority, prioritiesForDate, isTimerFor } from "@/lib/store";
import { metallicConfetti } from "@/lib/confetti";
import { useRef, useState } from "react";
import { toast } from "sonner";
import TaskDialog from "@/components/TaskDialog";

function fmtSpent(sec) {
  const s = Math.max(0, Math.floor(sec || 0));
  if (s < 60) return `${s} s`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h} h${m ? ` ${m} min` : ""}` : `${m} min`;
}

export default function TaskItem({ task, showTime = true, compact = false }) {
  const cat = findCategory(task.categoryId);
  const rowRef = useRef(null);
  const [editOpen, setEditOpen] = useState(false);
  const timer = useStore((s) => s.timer);
  const isRunning = isTimerFor(timer, task);
  const occurrenceDate = task._occurrenceDate || task.date;
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `${task.id}|${occurrenceDate || "none"}`,
    data: { type: "task", task, occurrenceDate }
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.4 : 1,
    touchAction: "manipulation"
  };

  function onToggle() {
    const willBeDone = !task.done;
    // Compute confetti intensity BEFORE mutating
    let intensity = 1;
    if (willBeDone && task.priority && occurrenceDate) {
      intensity = 2;
      const remainingPri = prioritiesForDate(occurrenceDate).filter((t) => !t.done).length;
      if (remainingPri === 1) intensity = 3;
    }
    toggleTaskDone(task.id, occurrenceDate);
    if (willBeDone) {
      requestAnimationFrame(() => metallicConfetti(rowRef.current, intensity));
    }
  }
  function onStar(e) {
    e.stopPropagation();
    const r = toggleTaskPriority(task.id, occurrenceDate);
    if (!r.ok && r.reason === "limit") {
      toast.error("Massimo 3 priorità al giorno", { description: "Rimuovi una priorità per aggiungerne un'altra." });
    }
  }
  function onTimerToggle(e) {
    e.stopPropagation();
    if (isRunning) stopTimer(); else startTimer(task.id, task._occurrenceDate || task.date);
  }

  return (
    <>
      <div
        ref={(node) => { setNodeRef(node); rowRef.current = node; }}
        style={style}
        className={`group flex items-center gap-2 md:gap-3 ${compact ? "py-1.5 px-2" : "py-2.5 px-2 md:px-3"} rounded-xl hover:bg-[#F3F5F0] transition-colors min-w-0`}
        data-testid={`task-row-${task.id}`}
      >
        <button
          {...attributes}
          {...listeners}
          className="hidden sm:inline-flex text-[#6A7368] hover:text-[#58635B] cursor-grab active:cursor-grabbing touch-none flex-shrink-0"
          data-testid={`task-drag-${task.id}`}
          aria-label="Sposta"
          type="button"
        >
          <GripVertical size={14} strokeWidth={1.5} />
        </button>
        <button
          type="button"
          className="tl-check flex-shrink-0"
          data-checked={task.done ? "true" : "false"}
          onClick={onToggle}
          data-testid={`task-check-${task.id}`}
          aria-label={task.done ? "Segna come da fare" : "Completa"}
        >
          <Check size={12} strokeWidth={3} color="#ffffff" />
        </button>
        <button
          type="button"
          className="flex-1 min-w-0 text-left"
          onClick={() => setEditOpen(true)}
          data-testid={`task-open-${task.id}`}
          aria-label="Modifica attività"
        >
          <div className={`text-sm ${task.done ? "tl-strike" : "text-[#3B443D]"} break-words`} data-testid={`task-title-${task.id}`}>
            {task.title}
          </div>
          {!compact && (
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <span className="tl-chip" style={{ background: cat.color + "26", color: "#58635B" }}>
                <span className="tl-chip-dot" style={{ background: cat.color }} />
                {cat.name}
              </span>
              {showTime && task.time && (
                <span className="inline-flex items-center gap-1 text-[11px] text-[#6A7368]">
                  <Clock size={11} strokeWidth={1.5} />
                  {task.time}
                </span>
              )}
              {task.durationMin ? (
                <span className="text-[11px] text-[#6A7368]">{task.durationMin}m stimati</span>
              ) : null}
              {(task.timeSpentSec > 0 || isRunning) && (
                <span className="text-[11px] text-[#8FA087] tabular-nums" data-testid={`task-spent-${task.id}`}>
                  {fmtSpent(task.timeSpentSec)} effettivi
                </span>
              )}
              {task.recurrence && task.recurrence !== "none" && (
                <span className="text-[11px] text-[#C48B97]">
                  {task.recurrence === "daily" ? "· ogni giorno" : task.recurrence === "weekdays" ? "· feriali" : "· settimanale"}
                </span>
              )}
            </div>
          )}
        </button>
        <button
          type="button"
          onClick={onStar}
          className={`flex-shrink-0 transition-colors ${task.priority ? "text-[#D4A94A]" : "text-[#6A7368] hover:text-[#D4A94A]"}`}
          data-testid={`task-star-${task.id}`}
          aria-label={task.priority ? "Rimuovi priorità" : "Segna come priorità"}
        >
          <Star size={16} strokeWidth={1.5} fill={task.priority ? "#D4A94A" : "none"} />
        </button>
        <button
          type="button"
          onClick={onTimerToggle}
          className={`flex-shrink-0 transition-colors ${isRunning ? "text-[#8FA087]" : "text-[#6A7368] hover:text-[#8FA087]"}`}
          data-testid={`task-timer-${task.id}`}
          aria-label={isRunning ? "Ferma cronometro" : "Avvia cronometro"}
        >
          {isRunning ? <Pause size={16} strokeWidth={1.5} /> : <Play size={16} strokeWidth={1.5} />}
        </button>
        <button
          type="button"
          onClick={() => setEditOpen(true)}
          className="hidden md:inline-flex opacity-0 group-hover:opacity-100 text-[#6A7368] hover:text-[#58635B] transition-opacity"
          data-testid={`task-edit-${task.id}`}
          aria-label="Modifica"
        >
          <Pencil size={14} strokeWidth={1.5} />
        </button>
      </div>
      <TaskDialog open={editOpen} onClose={() => setEditOpen(false)} task={task} />
    </>
  );
}
